import { supabase } from '../lib/supabase';
import { storage } from './storage';
import { StripeConfig } from '../types';

/**
 * Récupère la configuration Stripe (sans les clés secrètes)
 */
export const getStripeConfig = async (): Promise<StripeConfig | null> => {
  try {
    const config = await storage.getSystemConfig('stripe_config') as StripeConfig | null;
    if (!config) return null;
    
    // Ne pas exposer les clés secrètes côté client
    return {
      ...config,
      apiKeyTest: config.apiKeyTest ? '***' : undefined,
      apiKeyProd: config.apiKeyProd ? '***' : undefined,
    };
  } catch (error: any) {
    console.error('Error getting Stripe config:', error);
    return null;
  }
};

/**
 * Ajoute un item à l'abonnement (utilisateur national ou local)
 */
export const addSubscriptionItem = async (
  accessLevel: 'national' | 'local',
  userEmail?: string,
  userName?: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { data, error } = await supabase.functions.invoke('stripe-add-subscription-item', {
      body: { accessLevel, userEmail, userName },
    });

    if (error) {
      return { success: false, error: error.message || 'Erreur lors de l\'ajout à l\'abonnement' };
    }

    if (!data || !data.success) {
      return { success: false, error: data?.error || 'Erreur lors de l\'ajout à l\'abonnement' };
    }

    return { success: true };
  } catch (error: any) {
    console.error('Error adding subscription item:', error);
    return { success: false, error: error.message || 'Erreur lors de l\'ajout à l\'abonnement' };
  }
};

/**
 * Retire un item de l'abonnement
 */
export const removeSubscriptionItem = async (
  accessLevel: 'national' | 'local',
  userEmail?: string,
  userName?: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { data, error } = await supabase.functions.invoke('stripe-remove-subscription-item', {
      body: { accessLevel, userEmail, userName },
    });

    if (error) {
      return { success: false, error: error.message || 'Erreur lors de la suppression de l\'abonnement' };
    }

    if (!data || !data.success) {
      return { success: false, error: data?.error || 'Erreur lors de la suppression de l\'abonnement' };
    }

    return { success: true };
  } catch (error: any) {
    console.error('Error removing subscription item:', error);
    return { success: false, error: error.message || 'Erreur lors de la suppression de l\'abonnement' };
  }
};

/**
 * Récupère les items actuels de l'abonnement
 */
export const getSubscriptionItems = async (): Promise<{
  items: Array<{ priceId: string; quantity: number; type: 'national' | 'local' | 'unknown' }>;
  error?: string;
}> => {
  try {
    const { data, error } = await supabase.functions.invoke('stripe-get-subscription-items');

    if (error) {
      return { items: [], error: error.message || 'Erreur lors de la récupération des items' };
    }

    return { items: data?.items || [] };
  } catch (error: any) {
    console.error('Error getting subscription items:', error);
    return { items: [], error: error.message || 'Erreur lors de la récupération des items' };
  }
};

/**
 * Teste la connexion Stripe
 */
export const testStripeConnection = async (): Promise<{ success: boolean; error?: string }> => {
  try {
    const { data, error } = await supabase.functions.invoke('stripe-test-connection');

    if (error) {
      return { success: false, error: error.message || 'Erreur de connexion à Stripe' };
    }

    return { success: data?.success || false };
  } catch (error: any) {
    console.error('Error testing Stripe connection:', error);
    return { success: false, error: error.message || 'Erreur de connexion à Stripe' };
  }
};

/**
 * Récupère les prix Stripe (montant et devise)
 */
export const getStripePrices = async (): Promise<{
  prices: {
    national?: { id: string; amount: number; currency: string; recurring?: { interval: string; intervalCount: number } | null; error?: string };
    local?: { id: string; amount: number; currency: string; recurring?: { interval: string; intervalCount: number } | null; error?: string };
  };
  error?: string;
}> => {
  try {
    const { data, error } = await supabase.functions.invoke('stripe-get-prices');

    if (error) {
      return { prices: {}, error: error.message || 'Erreur lors de la récupération des prix' };
    }

    return { prices: data?.prices || {} };
  } catch (error: any) {
    console.error('Error getting Stripe prices:', error);
    return { prices: {}, error: error.message || 'Erreur lors de la récupération des prix' };
  }
};
